import { useMantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { useQuery } from '@tanstack/react-query';
import { getAllPreFillableFormsTemplates } from '@dealstack/frontend/shared/dataExchange/services/god.service.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { DataPreFillableFormTemplateWithProjectsTct } from '@dealstack/shared/lib/generated';
import { useMemo } from 'react';
import { getUserFullName } from '@dealstack/shared/lib/format/string.ts';
import { createDateColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/DateColumn';
import { ProjectsBadges } from '@dealstack/frontend/admin/pages/common-ui/ProjectsBadges';
import { PRE_FILLABLE_FORM_TEMPLATE_TYPE_NORMALIZED_NAME } from '@dealstack/shared/lib/common/business/preFillableForms.ts';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { Button } from '@mantine/core';
import { generatePath, useNavigate } from 'react-router-dom';
import { MANAGE_PRE_FILLABLE_FORMS_ROUTES } from '@dealstack/frontend/admin/pages/Manage/ManagePreFillableForms/ManagePreFillableForms.tsx';
import { GODMODE_ROOT_ROUTES } from '@dealstack/frontend/admin/pages/Routing/Routing.tsx';

const EditButton = ({ templateId }: { templateId: string }) => {
	const navigate = useNavigate();

	return (
		<Button
			variant="ghost"
			onClick={() => {
				navigate(
					generatePath(
						`${GODMODE_ROOT_ROUTES.MANAGE_PRE_FILLABLE_FORMS}/${MANAGE_PRE_FILLABLE_FORMS_ROUTES.EDIT}`,
						{ templateId }
					)
				);
			}}
		>
			Edit
		</Button>
	);
};

const createPreFillableFormsActionColumn =
	(): ExtendedColumnDef<DataPreFillableFormTemplateWithProjectsTct> => {
		return {
			cell: ({ row }) => {
				return <EditButton templateId={row.original.id} />;
			},
			header: '',
			id: 'actions',
			enableGlobalFilter: false,
			enableColumnFilter: false,
			filter: () => <div />,
			size: 80,
			meta: {
				isGrow: true,
			},
		};
	};

export const preFillableFormsTableColumns: ExtendedColumnDef<DataPreFillableFormTemplateWithProjectsTct>[] =
	[
		createTextColumn({ id: 'name', headerText: 'Template name', accessorKey: 'name' }),
		createTextColumn({
			id: 'type',
			headerText: 'Template type',
			accessorKey: 'type',
			accessorFn: ({ type }) => PRE_FILLABLE_FORM_TEMPLATE_TYPE_NORMALIZED_NAME[type],
		}),
		createTextColumn({
			id: 'assignedProjects',
			headerText: 'Assigned deals',
			accessorFn: ({ preFillableFormTemplatesProjects }) =>
				preFillableFormTemplatesProjects.map(
					(projectRelation) => projectRelation.deal.name
				),
			cell: ({ cell }) => <ProjectsBadges projectsNames={cell.getValue<string[]>()} />,
			filterFn: 'includesString',
			enableSorting: false,
		}),
		createTextColumn({
			id: 'assignedOrganizations',
			headerText: 'Assigned organisations',
			accessorFn: ({ assignedOrganizationNames }) => assignedOrganizationNames,
			cell: ({ cell }) => <ProjectsBadges projectsNames={cell.getValue<string[]>()} />,
			filterFn: 'includesString',
			enableSorting: false,
		}),
		createTextColumn({
			id: 'createdBy',
			headerText: 'Created by',
			accessorFn: ({ createdBy }) => getUserFullName(createdBy),
		}),
		createDateColumn({
			id: 'createdAt',
			headerText: 'Created at',
			accessorFn: ({ createdAt }) => !!createdAt && new Date(createdAt),
			sortingFn: 'datetime',
		}),
		createDateColumn({
			id: 'updatedAt',
			headerText: 'Updated at',
			accessorFn: ({ updatedAt }) => !!updatedAt && new Date(updatedAt),
			sortingFn: 'datetime',
		}),
		createPreFillableFormsActionColumn(),
	];

export const usePreFillableFormsTableController = () => {
	const getAllTemplatesRequest = useQuery({
		queryKey: [API_PATHS.GODMODE_PRE_FILLABLE_FORM_TEMPLATE_LIST_GET],
		queryFn: ({ signal }) =>
			getAllPreFillableFormsTemplates(
				API_PATHS.GODMODE_PRE_FILLABLE_FORM_TEMPLATE_LIST_GET,
				signal
			),
	});

	const mantineTableDataOperations = useMantineTableDataOperations();

	const tableData = useMemo(() => {
		return getAllTemplatesRequest.data?.payload ?? [];
	}, [getAllTemplatesRequest.data?.payload]);

	return {
		tableData,
		isLoading: getAllTemplatesRequest.isFetching,
		mantineTableDataOperations,
	};
};
